/* Formulario de una solicitud de mantenimiento correctivo a GafCORE.
 *
 * Primero se busca el equipo y se muestra solo su codigo y su nombre; despues
 * se cargan los campos. EMSYS y GafCORE vuelven a validar todo al enviar.
 * El equipo se busca de dos formas:
 *   Por PS      se elige el PS y se toca el equipo de su lista. El fiscal ve sus
 *               PS y el jefe los de sus fiscales; el resto busca cualquiera
 *               (lo decide EMSYS).
 *   Por codigo  codigo G, de Fracttal o nombre.
 *
 * Mismo patron que SolicitudGafcoreFormulario (traslados): una IonCard por
 * seccion, IonItem + IonLabel stacked por campo y el buscador de Solicitudes a RRHH.
 */
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonDatetime,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  IonTextarea,
  IonToggle,
} from "@ionic/react";
import { cameraOutline, closeCircleOutline, swapHorizontalOutline } from "ionicons/icons";
import { useEffect, useMemo, useRef, useState } from "react";

import { isoHoyMas } from "src/components/SolicitudCapacitacionCampos";
import SolicitudRRHHBuscador, { OpcionBuscada } from "src/components/SolicitudRRHHBuscador";
import {
  achicarFoto,
  ActivoMantenimiento,
  CatalogoMantenimientos,
  MantenimientosAPI,
  PuntoServicioMantenimiento,
} from "src/services/mantenimientos";
import { LugarGafcore } from "src/services/traslados";

const tarjeta: React.CSSProperties = { margin: "0 0 16px" };
// Dentro de IonCardContent el IonItem ya tiene el margen de la tarjeta.
const sinSangria = { "--padding-start": "0" } as React.CSSProperties;

// IonDatetime devuelve un ISO completo; EMSYS espera "YYYY-MM-DD HH:mm" (hora local).
const fechaHora = (v?: string | null) => {
  if (!v) return "";
  const texto = String(v);
  return `${texto.substring(0, 10)} ${texto.substring(11, 16)}`;
};

const opcionActivo = (a: ActivoMantenimiento): OpcionBuscada => ({
  id: a.codigo_g,
  titulo: `${a.codigo_g}${a.cod_fracttal ? ` / ${a.cod_fracttal}` : ""}`,
  detalle: a.nombre,
});

const opcionLugar = (l: LugarGafcore): OpcionBuscada => ({ id: l.codigo, titulo: l.nombre, detalle: l.codigo });

const opcionPs = (p: PuntoServicioMantenimiento): OpcionBuscada => ({
  id: p.codigo,
  titulo: p.nombre,
  detalle:
    p.en_gafcore === false
      ? `${p.codigo} · no figura en GafCORE`
      : `${p.codigo}${p.equipos != null ? ` · ${p.equipos} equipo${p.equipos === 1 ? "" : "s"}` : ""}`,
});

// Un PS puede tener cientos de equipos: se listan los primeros y se filtra.
const MAX_VISIBLES = 30;

interface Props {
  catalogo: CatalogoMantenimientos;
  /** Devuelve true si salió bien (la pantalla cambia de pestaña). */
  onEnviar: (payload: Record<string, any>) => Promise<boolean>;
  onError: (mensaje: string) => void;
}

const SolicitudMantenimientoFormulario: React.FC<Props> = ({ catalogo, onEnviar, onError }) => {
  const [modo, setModo] = useState<"ps" | "codigo">("ps");
  const [alcance, setAlcance] = useState<"propios" | "todos">("todos");
  const [puntos, setPuntos] = useState<PuntoServicioMantenimiento[]>([]);
  const [ps, setPs] = useState<PuntoServicioMantenimiento | null>(null);
  const [enPs, setEnPs] = useState<ActivoMantenimiento[]>([]);
  const [cargandoPs, setCargandoPs] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [encontrados, setEncontrados] = useState<ActivoMantenimiento[]>([]);
  const [equipo, setEquipo] = useState<ActivoMantenimiento | null>(null);
  const [lugar, setLugar] = useState<LugarGafcore | null>(null);
  const [lugares, setLugares] = useState<LugarGafcore[]>([]);
  const [paro, setParo] = useState(false);
  const [fecha, setFecha] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [foto, setFoto] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const inputFoto = useRef<HTMLInputElement>(null);

  // Por PS: elegido el PS se listan sus equipos y se toca uno.
  useEffect(() => {
    MantenimientosAPI.puntosServicio()
      .then((r) => {
        setAlcance(r.data.data.alcance);
        setPuntos(r.data.data.puntos || []);
      })
      .catch((err) => onError(err?.response?.data?.error || "No se pudieron traer los Puntos de Servicio"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setEnPs([]);
    setFiltro("");
    if (!ps) return;
    let cancelado = false;
    setCargandoPs(true);
    MantenimientosAPI.activos({ ubicacion: ps.codigo })
      .then((r) => !cancelado && setEnPs(r.data.data.activos || []))
      .catch((err) => !cancelado && onError(err?.response?.data?.error || "No se pudieron traer los equipos del PS"))
      .finally(() => !cancelado && setCargandoPs(false));
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ps]);

  const filtrados = useMemo(() => {
    const f = filtro.trim().toUpperCase();
    return enPs.filter((a) => !f || `${a.codigo_g} ${a.cod_fracttal || ""} ${a.nombre}`.toUpperCase().includes(f));
  }, [enPs, filtro]);

  const elegirEquipo = (a: ActivoMantenimiento | null) => {
    setEquipo(a);
    setEncontrados([]);
    // "¿Dónde está?" arranca en el lugar donde lo tiene GafCORE.
    setLugar(
      a?.ubicacion_codigo
        ? { codigo: a.ubicacion_codigo, nombre: a.ubicacion_nombre || a.ubicacion_codigo, vigente: true, es_ps: true }
        : null
    );
  };

  const listaLugares = lugar && !lugares.some((l) => l.codigo === lugar.codigo) ? [lugar, ...lugares] : lugares;

  const enviar = async () => {
    if (!equipo) return onError("Buscá y elegí el equipo.");
    if (!equipo.ubicacion_codigo && !lugar) return onError("Indicá dónde está el equipo.");
    if (!descripcion.trim()) return onError("Contá qué le pasa al equipo.");

    setEnviando(true);
    const ok = await onEnviar({
      codigo: equipo.codigo_g,
      nombre: equipo.nombre,
      // Solo si dijo otro lugar: si es donde figura, GafCORE ya lo sabe.
      ubicacion: lugar && lugar.codigo !== equipo.ubicacion_codigo ? lugar.codigo : "",
      paro_equipo: paro,
      fecha_incidente: fecha,
      descripcion: descripcion.trim(),
      foto: foto || "",
    });
    if (!ok) setEnviando(false);
  };

  return (
    <>
      <IonCard style={tarjeta}>
        <IonCardHeader>
          <IonCardSubtitle>¿Qué equipo tiene la falla?</IonCardSubtitle>
        </IonCardHeader>
        <IonCardContent style={{ paddingTop: 0 }}>
          {equipo ? (
            <IonItem lines="none" style={sinSangria}>
              <IonLabel className="ion-text-wrap">
                <h2 style={{ fontWeight: 600 }}>{equipo.nombre}</h2>
                <p>
                  {equipo.codigo_g}
                  {equipo.cod_fracttal ? ` · Fracttal ${equipo.cod_fracttal}` : ""}
                </p>
              </IonLabel>
              <IonButton fill="clear" slot="end" disabled={enviando} onClick={() => elegirEquipo(null)}>
                <IonIcon icon={swapHorizontalOutline} slot="start" />
                Cambiar
              </IonButton>
            </IonItem>
          ) : (
            <>
              <IonSegment
                value={modo}
                onIonChange={(e) => setModo((e.detail.value as "ps" | "codigo") || "ps")}
                style={{ marginBottom: 8 }}
              >
                <IonSegmentButton value="ps">
                  <IonLabel>Por PS</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="codigo">
                  <IonLabel>Por código</IonLabel>
                </IonSegmentButton>
              </IonSegment>

              {modo === "ps" ? (
                <>
                  <IonNote color="medium" style={{ display: "block", marginBottom: 4 }}>
                    {alcance === "propios"
                      ? "Elegí uno de tus Puntos de Servicio y después el equipo."
                      : "Buscá el Punto de Servicio y después elegí el equipo."}
                  </IonNote>
                  {/* Los propios ya vienen todos y se filtran aca; el resto se busca en el servidor. */}
                  <SolicitudRRHHBuscador
                    opciones={(ps && !puntos.some((p) => p.codigo === ps.codigo) ? [ps, ...puntos] : puntos).map(opcionPs)}
                    valor={ps?.codigo || ""}
                    onElegir={(id) => setPs(puntos.find((p) => p.codigo === id) || null)}
                    placeholder="Buscar Punto de Servicio"
                    onBuscar={
                      alcance === "propios"
                        ? undefined
                        : (q) =>
                            MantenimientosAPI.puntosServicio(q)
                              .then((r) => setPuntos(r.data.data.puntos || []))
                              .catch((err) =>
                                onError(err?.response?.data?.error || "No se pudieron buscar los Puntos de Servicio")
                              )
                    }
                  />

                  {ps && (
                    <>
                      <IonSearchbar
                        value={filtro}
                        placeholder="Filtrar equipos por código o nombre"
                        onIonInput={(e) => setFiltro((e.target as HTMLIonSearchbarElement).value || "")}
                        onIonClear={() => setFiltro("")}
                        style={{ paddingInline: 0 }}
                        disabled={cargandoPs}
                      />
                      {cargandoPs ? (
                        <div style={{ textAlign: "center", padding: 16 }}>
                          <IonSpinner />
                        </div>
                      ) : !filtrados.length ? (
                        <IonText color="medium">
                          <p style={{ margin: "0 0 8px" }}>
                            {enPs.length
                              ? "Ningún equipo coincide con el filtro."
                              : "GafCORE no registra equipos en este PS. Probá buscarlo por código."}
                          </p>
                        </IonText>
                      ) : (
                        <IonList lines="full">
                          {filtrados.slice(0, MAX_VISIBLES).map((a) => (
                            <IonItem
                              key={a.codigo_g}
                              button
                              detail={false}
                              onClick={() => elegirEquipo(a)}
                              style={sinSangria}
                            >
                              <IonLabel className="ion-text-wrap">
                                <h3>
                                  {a.codigo_g}
                                  {a.cod_fracttal ? ` / ${a.cod_fracttal}` : ""}
                                </h3>
                                <p>{a.nombre}</p>
                              </IonLabel>
                            </IonItem>
                          ))}
                        </IonList>
                      )}
                      {filtrados.length > MAX_VISIBLES && (
                        <IonText color="medium">
                          <p style={{ margin: "4px 0 8px" }}>
                            Se muestran {MAX_VISIBLES} de {filtrados.length}. Filtrá para encontrar el resto.
                          </p>
                        </IonText>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <IonNote color="medium" style={{ display: "block", marginBottom: 4 }}>
                    Código G (G-0004137), código de Fracttal (MOFIC1055) o parte del nombre.
                  </IonNote>
                  <SolicitudRRHHBuscador
                    opciones={encontrados.map(opcionActivo)}
                    valor=""
                    onElegir={(id) => elegirEquipo(encontrados.find((a) => a.codigo_g === id) || null)}
                    placeholder="Buscar equipo"
                    minimo={3}
                    onBuscar={(q) =>
                      MantenimientosAPI.activos({ q })
                        .then((r) => setEncontrados(r.data.data.activos || []))
                        .catch((err) => onError(err?.response?.data?.error || "No se pudieron buscar los equipos"))
                    }
                  />
                </>
              )}
            </>
          )}
        </IonCardContent>
      </IonCard>

      {equipo && (
        <>
          <IonCard style={tarjeta}>
            <IonCardHeader>
              <IonCardSubtitle>La solicitud</IonCardSubtitle>
            </IonCardHeader>
            <IonList lines="full">
              <IonItem>
                <IonLabel className="ion-text-wrap">
                  <h2>¿Está parado el equipo?</h2>
                  <p>Si no se puede usar, GAF lo toma como urgente.</p>
                </IonLabel>
                <IonToggle slot="end" checked={paro} disabled={enviando} onIonChange={(e) => setParo(e.detail.checked)} />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">¿Desde cuándo falla? (opcional)</IonLabel>
                <IonDatetime
                  displayFormat="DD/MM/YYYY HH:mm"
                  pickerFormat="DD/MM/YYYY HH:mm"
                  cancelText="Cancelar"
                  doneText="Listo"
                  placeholder="Seleccionar fecha y hora"
                  min={isoHoyMas(-365)}
                  max={isoHoyMas(0)}
                  value={fecha ? fecha.replace(" ", "T") : undefined}
                  onIonChange={(e) => setFecha(fechaHora(e.detail.value))}
                  disabled={enviando}
                />
              </IonItem>
              <IonItem lines="none">
                <IonLabel position="stacked">¿Qué le pasa? *</IonLabel>
                <IonTextarea
                  value={descripcion}
                  placeholder="La falla, en palabras. Por ejemplo: no aspira y hace ruido al prender."
                  onIonChange={(e) => setDescripcion(e.detail.value || "")}
                  autoGrow
                  rows={3}
                  maxlength={4000}
                  disabled={enviando}
                />
              </IonItem>
            </IonList>
          </IonCard>

          <IonCard style={tarjeta}>
            <IonCardHeader>
              <IonCardSubtitle>¿Dónde está el equipo?</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent style={{ paddingTop: 0 }}>
              <IonNote color="medium" style={{ display: "block", marginBottom: 4 }}>
                {equipo.ubicacion_codigo
                  ? "Es donde lo tiene GafCORE. Si está en otro lugar, elegí el correcto: el técnico va a ese."
                  : "GafCORE no sabe dónde está: elegí el lugar. *"}
              </IonNote>
              <SolicitudRRHHBuscador
                opciones={listaLugares.map(opcionLugar)}
                valor={lugar?.codigo || ""}
                onElegir={(id) => setLugar(listaLugares.find((l) => l.codigo === id) || null)}
                placeholder="Buscar PS por nombre o código"
                onBuscar={(q) =>
                  MantenimientosAPI.lugares(q)
                    .then((r) => setLugares(r.data.data || []))
                    .catch((err) => onError(err?.response?.data?.error || "No se pudieron buscar los lugares"))
                }
              />
            </IonCardContent>
          </IonCard>

          <IonCard style={tarjeta}>
            <IonCardHeader>
              <IonCardSubtitle>Foto de la falla (opcional)</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent style={{ paddingTop: 0 }}>
              <input
                ref={inputFoto}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: "none" }}
                onChange={(e) => {
                  const archivo = e.target.files?.[0];
                  if (inputFoto.current) inputFoto.current.value = "";
                  if (!archivo) return;
                  achicarFoto(archivo, catalogo.max_foto_bytes)
                    .then(setFoto)
                    .catch((err) => onError(err.message));
                }}
              />
              {foto ? (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <img src={foto} alt="Foto de la falla" style={{ maxHeight: 120, maxWidth: "60%", borderRadius: 8 }} />
                  <IonButton fill="clear" color="danger" disabled={enviando} onClick={() => setFoto(null)}>
                    <IonIcon icon={closeCircleOutline} slot="start" />
                    Quitar
                  </IonButton>
                </div>
              ) : (
                <>
                  <IonButton size="small" fill="outline" disabled={enviando} onClick={() => inputFoto.current?.click()}>
                    <IonIcon icon={cameraOutline} slot="start" />
                    Agregar foto
                  </IonButton>
                  <IonNote color="medium" style={{ display: "block", marginTop: 4 }}>
                    Ayuda a GAF a decidir sin ir hasta el equipo. Se achica sola si es muy grande.
                  </IonNote>
                </>
              )}
            </IonCardContent>
          </IonCard>

          <IonButton expand="block" disabled={enviando} onClick={enviar}>
            {enviando ? <IonSpinner name="crescent" /> : "Enviar a GafCORE"}
          </IonButton>
        </>
      )}
    </>
  );
};

export default SolicitudMantenimientoFormulario;
